import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

import { getAuth } from "firebase/auth";
import type { AppDispatch, RootState } from "./store";
import { auth, db, storage } from "../firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/* ───────────────── CONSTANTS ───────────────── */

// safety caps for the "write your own book" flow — pages are stored
// as one array field on the book doc, so these keep a single doc
// from growing without bound
export const MAX_PAGE_CHARS = 700;
export const MAX_PAGES = 500;

/* ───────────────── TYPES ───────────────── */

export type BookPage = {
  id: string;
  text: string;
};

export type Book = {
  listingId: string;
  brandId: string;
  hostId: string;

  title?: string;
  author?: string;
  description?: string;

  categoryId?: number | null;
  subcategoryId?: number | null;
  productId?: number | null;

  // true  = author writes the book directly, readers flip through pages
  // false = physical / PDF copy sold with a cover photo, like a normal product
  isVisualBook: boolean;

  // physical-book fields
  price?: number;
  stock?: number;
  available?: boolean;
  coverPhoto?: string;
  photos?: string[];
  dimensions?: {
    unit: "cm" | "inch";
    weightUnit: "g" | "ml";
    height?: number;
    width?: number;
    length?: number;
    netWeight?: number;
  };

  // visual-book fields
  pages?: BookPage[];

  isPublished: boolean;
  isFlagged: boolean;
  isComplete: boolean;
  isReviewed: boolean;
  isDraft?: boolean;

  joinDate: string;
  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
};

type BookState = {
  books: Book[];
  currentBook: Book | null;
  unsubscribe?: () => void;
  loading: boolean;
  error: string | null;
};

const initialState: BookState = {
  books: [],
  currentBook: null,
  unsubscribe: undefined,
  loading: false,
  error: null,
};

/* ───────────────── HELPERS ───────────────── */

const toISO = (ts: any) =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

const parseBook = (id: string, raw: Record<string, any>): Book => {
  const { createdAt, updatedAt, ...safeRaw } = raw;

  return {
    listingId: id,
    brandId: raw.brandId ?? "",
    hostId: raw.hostId ?? "",

    title: raw.title ?? "",
    author: raw.author ?? "",
    description: raw.description ?? "",

    categoryId: raw.categoryId ?? null,
    subcategoryId: raw.subcategoryId ?? null,
    productId: raw.productId ?? null,

    isVisualBook: raw.isVisualBook ?? false,

    price: raw.price ?? 0,
    stock: raw.stock ?? 0,
    available: raw.available ?? true,
    coverPhoto: raw.coverPhoto ?? "",
    photos: raw.photos ?? [],
    dimensions: raw.dimensions ?? { unit: "cm", weightUnit: "g" },

    pages: raw.pages ?? [],

    isPublished: raw.isPublished ?? false,
    isFlagged: raw.isFlagged ?? false,
    isComplete: raw.isComplete ?? false,
    isReviewed: raw.isReviewed ?? false,
    isDraft: raw.isDraft ?? true,

    joinDate: raw.joinDate ?? new Date().toISOString(),

    createdAt: toISO(createdAt),
    updatedAt: toISO(updatedAt),

    ...safeRaw,
  };
};

/* ───────────────── UPSERT BOOK ───────────────── */

export const upsertBook = createAsyncThunk<
  Book,
  {
    listingId?: string;
    data: Omit<
      Partial<Book>,
      | "brandId"
      | "hostId"
      | "joinDate"
      | "createdAt"
      | "updatedAt"
      | "photos"
      | "coverPhoto"
    >;
  }
>("books/upsert", async ({ listingId, data }, { rejectWithValue }) => {
  try {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not authenticated");

    /* ── find brand ── */
    const partnerQuery = query(
      collection(db, "commercePartners"),
      where("userId", "==", user.uid)
    );

    const partnerSnap = await getDocs(partnerQuery);
    if (partnerSnap.empty) {
      throw new Error("No commerce partner found");
    }

    const brandId = partnerSnap.docs[0].id;

    /* ── clean data ── */
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined)
    );

    const baseData = {
      ...cleanData,
      brandId,
      hostId: user.uid,
      updatedAt: serverTimestamp(),
    };

    /* ── update existing ── */
    if (listingId) {
      const refDoc = doc(db, "books", listingId);
      await updateDoc(refDoc, baseData);

      const snap = await getDoc(refDoc);
      return parseBook(snap.id, snap.data() || {});
    }

    /* ── create new ── */
    const payload = {
      ...baseData,
      joinDate: new Date().toISOString(),
      createdAt: serverTimestamp(),
      isPublished: cleanData.isPublished ?? false,
      isDraft: cleanData.isDraft ?? !(cleanData.isPublished ?? false),
      isFlagged: false,
      isComplete: false,
      isReviewed: false,
    };

    const colRef = collection(db, "books");
    const newDoc = await addDoc(colRef, payload);
    await updateDoc(newDoc, { listingId: newDoc.id });

    const snap = await getDoc(newDoc);
    return parseBook(newDoc.id, snap.data() || {});
  } catch (err: any) {
    return rejectWithValue(err.message || "upsert failed");
  }
});

/* ───────────────── LISTENERS ───────────────── */

/* Listen to all books owned by the current host (seller dashboard) */
export const listenToHostBooks =
  ({ hostId }: { hostId: string }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;

    const { unsubscribe } = getState().books;

    /* always clean up the previous listener */
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(collection(db, "books"), where("hostId", "==", hostId));

    const unsub = onSnapshot(q, (snap) => {
      dispatch(setBooks(snap.docs.map((d) => parseBook(d.id, d.data()))));
    });

    dispatch(setUnsubscribe(unsub));
  };

/* Listen to a single book document */
export const listenToBookData =
  (listingId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!listingId) return;

    const { unsubscribe } = getState().books;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const refDoc = doc(db, "books", listingId);

    const unsub = onSnapshot(refDoc, (snap) => {
      if (!snap.exists()) {
        dispatch(setCurrentBook(null));
        return;
      }
      dispatch(setCurrentBook(parseBook(snap.id, snap.data())));
    });

    dispatch(setUnsubscribe(unsub));
  };

/* ───────────────── MEDIA ───────────────── */

/* Upload a cover photo or extra photos for a book */
export const upsertBookMedia = createAsyncThunk<
  { listingId: string; downloadURLs: string[] },
  { listingId?: string; files: File[]; type: "cover" | "photo" }
>("books/upsertMedia", async ({ listingId, files, type }, { dispatch }) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Not logged in");

  let id = listingId;
  const uploaded: string[] = [];

  for (const file of files) {
    const folder = type === "cover" ? "cover" : "photos";
    const path = `books/${id || "new"}/${folder}/${file.name}`;

    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    uploaded.push(await getDownloadURL(storageRef));
  }

  if (!id) {
    const created = await dispatch(
      upsertBook({
        data: {
          coverPhoto: type === "cover" ? uploaded[0] : "",
          photos: type === "photo" ? uploaded : [],
        },
      })
    ).unwrap();

    id = created.listingId;
  } else {
    await dispatch(
      upsertBook({
        listingId: id,
        data:
          type === "cover"
            ? { coverPhoto: uploaded[0] }
            : { photos: uploaded },
      })
    );
  }

  return { listingId: id!, downloadURLs: uploaded };
});

/* Delete a single extra photo */
export const deleteBookPhoto = createAsyncThunk<
  string,
  { listingId: string; photoUrl: string }
>("books/deletePhoto", async ({ listingId, photoUrl }) => {
  const refDoc = doc(db, "books", listingId);
  const snap = await getDoc(refDoc);

  if (!snap.exists()) throw new Error("Book not found");

  const photos = snap.data()?.photos || [];

  await updateDoc(refDoc, {
    photos: photos.filter((p: string) => p !== photoUrl),
  });

  if (photoUrl.includes("/o/")) {
    const encoded = photoUrl.split("/o/")[1].split("?")[0];
    await deleteObject(ref(storage, decodeURIComponent(encoded)));
  }

  return photoUrl;
});

/* ───────────────── DELETE ───────────────── */

export const deleteBook = createAsyncThunk<{ listingId: string }, string>(
  "books/delete",
  async (listingId) => {
    await deleteDoc(doc(db, "books", listingId));
    return { listingId };
  }
);

/* ───────────────── SLICE ───────────────── */

const bookSlice = createSlice({
  name: "books",
  initialState,
  reducers: {
    setBooks: (state, action: PayloadAction<Book[]>) => {
      state.books = action.payload;
    },
    setCurrentBook: (state, action: PayloadAction<Book | null>) => {
      state.currentBook = action.payload;
    },
    setUnsubscribe: (
      state,
      action: PayloadAction<(() => void) | undefined>
    ) => {
      state.unsubscribe = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(upsertBook.fulfilled, (state, action) => {
        const idx = state.books.findIndex(
          (b) => b.listingId === action.payload.listingId
        );
        if (idx !== -1) state.books[idx] = action.payload;
        else state.books.push(action.payload);
      })
      .addCase(deleteBook.fulfilled, (state, action) => {
        state.books = state.books.filter(
          (b) => b.listingId !== action.payload.listingId
        );
      });
  },
});

export const { setBooks, setCurrentBook, setUnsubscribe, setError } =
  bookSlice.actions;

export default bookSlice.reducer;

/*
  Don't forget to register the reducer in your store, e.g.:

  import booksReducer from "./bookSlice";
  export const store = configureStore({
    reducer: {
      ...
      books: booksReducer,
    },
  });
*/
